import { useEffect, useState } from 'react';

/**
 * A media query as state, so one layout decision can be made in JS and in CSS from one line.
 *
 * The breakpoints below are also written in `styles.css`, and the rule is that a component may
 * only branch on one that has a constant here. The Tasks screen's inline capture box stops
 * rendering at `PHONE` while the header's `+` starts appearing at the same width; two
 * nearly-equal breakpoints would show both inputs at once in the gap between them, which is
 * precisely what the disclosure exists to avoid.
 */
export function useMedia(query: string): boolean {
  const [matches, setMatches] = useState(
    () => typeof matchMedia === 'function' && matchMedia(query).matches,
  );
  useEffect(() => {
    if (typeof matchMedia !== 'function') return;
    const mq = matchMedia(query);
    const onChange = () => setMatches(mq.matches);
    setMatches(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [query]);
  return matches;
}

/** The breakpoint below which the workspace stops being two columns. Matches `styles.css`. */
export const NARROW = '(max-width: 860px)';

/**
 * Phone width, where the chrome above the first task has to be paid for.
 *
 * Measured on a real account: the first task row began at y=352 of an 812px screen — 43% of it —
 * with nine rows fitting underneath. Matches the `40rem` queries in `styles.css`.
 */
export const PHONE = '(max-width: 40rem)';

/**
 * The width below which Today's three panes become tabs and only one is on screen.
 *
 * Matches the `60rem` queries in `styles.css`. A component needs this because "is the timeline on
 * screen" is not answerable from CSS alone: the shortlist was printing "Drag onto the timeline to
 * plan it for this day" on a 375px phone, where the timeline is a different tab and a DOM probe
 * returned a `.timeline` rect of 0×0 — an instruction naming something not on the page.
 */
export const PANES_STACK = '(max-width: 60rem)';
